import { Alert, Button, Card, Flex, Progress, Stack, Table, Text } from '@mantine/core';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { IntakeAction, IntakeAnswers, FormatPreference, TherapyBooking } from '../types';
import { getTherapyTypesForSubmission, isHighRisk } from '../types';
import { AVAILABILITY_DAYS_OPTIONS, AVAILABILITY_TIME_OPTIONS } from '../bookingConstraints';
import { THERAPY_TYPE_OPTIONS } from '../therapyTypeOptions';
import { useIntakeDataContext } from '../IntakeDataContext';
import { type IntakeServiceCatalogEntry, findIntakeServiceForTherapyType } from '../intakeServicesCatalog';

function availabilityTimeLabel(value: string): string {
  return AVAILABILITY_TIME_OPTIONS.find((o) => o.value === value)?.label ?? value;
}
function availabilityDaysLabel(value: string): string {
  return AVAILABILITY_DAYS_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

interface StepConfirmationProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  isCompleting?: boolean;
  redirectSecondsRemaining?: number;
  /**
   * Cancel/restore a booked appointment. When cancelling, the parent also releases that booking's
   * referral slot + held calendar Slot via the ticketing bot. Falls back to a plain dispatch when omitted.
   */
  onToggleBookingCancelled?: (index: number, cancelled: boolean, booking: TherapyBooking) => void;
  /** Active service catalog for live per-booking cash pricing. */
  services?: IntakeServiceCatalogEntry[];
  /** When set, replaces the default "Redirecting home" summary message. */
  completionCloseLabel?: string;
}

function formatRate(cost: number | null | undefined): string | null {
  if (cost == null) return null;
  if (cost <= 0) return 'Free';
  return `$${Number.isInteger(cost) ? cost : cost.toFixed(2)}`;
}

function formatPreferenceLabel(
  pref: FormatPreference | null,
  providerFormats?: Array<'in-person' | 'virtual'>
): string {
  if (!pref) return '—';
  if (pref === 'in-person') return 'In person';
  if (pref === 'virtual') return 'Virtual';

  if (providerFormats && providerFormats.length === 1) {
    return providerFormats[0] === 'in-person' ? 'In person' : 'Virtual';
  }
  return 'No preference';
}

function getBookingDateTimeLabels(booking: TherapyBooking): { date: string; time: string } {
  if (!booking.bookingDate || !booking.bookingTime) return { date: '—', time: '—' };

  const dt = dayjs(`${booking.bookingDate}T${booking.bookingTime}`);
  return {
    date: dt.isValid() ? dt.format('dddd, MMM D, YYYY') : booking.bookingDate,
    time: dt.isValid() ? dt.format('h:mm A') : booking.bookingTime,
  };
}

function isWaitlistedBooking(booking: TherapyBooking, answers: IntakeAnswers): boolean {
  if (booking.waitlisted) return true;
  return (answers.waitlistedTherapyTypes ?? []).includes(booking.therapyType);
}

function reviewSubtitle(
  waitlistedCount: number,
  scheduledCount: number,
  pendingCount: number,
): string {
  if (waitlistedCount === 0 && scheduledCount === 0 && pendingCount > 0) {
    return pendingCount === 1
      ? 'Here is the service you requested. Our team will reach out to schedule your screening call.'
      : 'Here are the services you requested. Our team will reach out to schedule your screening call.';
  }
  if (waitlistedCount > 0 && scheduledCount > 0) {
    return `You have ${scheduledCount} scheduled appointment${scheduledCount === 1 ? '' : 's'} and ${waitlistedCount} service${waitlistedCount === 1 ? '' : 's'} on our waitlist.`;
  }
  if (waitlistedCount > 0) {
    return waitlistedCount === 1
      ? 'This service is on our waitlist. We will contact you when a provider becomes available.'
      : 'These services are on our waitlist. We will contact you when a provider becomes available.';
  }
  if (scheduledCount > 0) {
    return scheduledCount === 1
      ? 'Here is your scheduled appointment.'
      : `You have ${scheduledCount} appointments scheduled.`;
  }
  return 'You have no active services. Restore one below or go back to change your booking.';
}

export function StepConfirmation({
  answers,
  dispatch,
  isCompleting = false,
  redirectSecondsRemaining,
  onToggleBookingCancelled,
  services,
  completionCloseLabel,
}: StepConfirmationProps): JSX.Element {
  const { providers: allProviders } = useIntakeDataContext();

  const cashCostLabelFor = (booking: TherapyBooking): string => {
    const entry = findIntakeServiceForTherapyType(services ?? [], booking.therapyType);
    const cost =
      booking.sessionType === 'consultation'
        ? entry?.sessions.consultation?.cost
        : entry?.sessions.intake?.cost;
    return formatRate(cost) ?? 'Provided at booking';
  };

  if (isCompleting) {
    const totalSeconds = 10;
    const seconds =
      typeof redirectSecondsRemaining === 'number' && redirectSecondsRemaining >= 0
        ? redirectSecondsRemaining
        : null;
    const progressValue = seconds != null ? (seconds / totalSeconds) * 100 : 0;

    return (
      <Stack gap="xl" align="center" py="xl" style={{ maxWidth: 400, margin: '0 auto' }}>
        <IconCircleCheckFilled size={56} style={{ color: 'var(--mantine-color-gold-5)' }} />
        <Stack gap={6} align="center">
          <Text className="intake-step-title" fw={700} ta="center">
            You&apos;re all set!
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            Congratulations on starting your mental health journey.
          </Text>
        </Stack>
        <Stack gap={6} align="center" w="100%">
          <Progress value={progressValue} size={4} color="gold" w="60%" />
          <Text size="xs" c="dimmed" ta="center" aria-live="polite">
            {seconds != null
              ? completionCloseLabel
                ? completionCloseLabel.replace(/…$/, ` in ${seconds}s`)
                : `Redirecting home in ${seconds}s`
              : completionCloseLabel ?? 'Redirecting…'}
          </Text>
        </Stack>
      </Stack>
    );
  }

  const { bookings } = answers;
  const activeBookings = bookings.filter((b) => !b.cancelled);
  const waitlistedCount = activeBookings.filter((b) => isWaitlistedBooking(b, answers)).length;
  const scheduledCount = activeBookings.length - waitlistedCount;
  const highRisk = isHighRisk(answers);
  // Services that produce no appointment in intake (Group Therapy) still need to appear here,
  // otherwise the review is blank and the client can't tell what they requested.
  const pendingTherapyTypes = bookings.length === 0 ? getTherapyTypesForSubmission(answers) : [];

  const toggleWaitlisted = (idx: number, therapyType: TherapyBooking['therapyType'], cancelled: boolean) => {
    dispatch({ type: 'SET_BOOKING_CANCELLED', payload: { index: idx, cancelled } });
    dispatch({
      type: 'SET_THERAPY_TYPE_WAITLISTED',
      payload: { therapyType, waitlisted: !cancelled },
    });
    dispatch({
      type: 'SET_THERAPY_TYPE_CANCELLED',
      payload: { therapyType, cancelled },
    });
  };

  return (
    <Stack gap="lg" style={{ maxWidth: 540, margin: '0 auto', width: '100%' }}>
      <Text className="intake-step-title" fw={700} ta="center">
        Review your intake details
      </Text>
      <Text size="sm" ta="center" c="dimmed">
        {reviewSubtitle(waitlistedCount, scheduledCount, pendingTherapyTypes.length)}
      </Text>

      {pendingTherapyTypes.map((therapyType) => {
        const therapyLabel =
          THERAPY_TYPE_OPTIONS.find((o) => o.value === therapyType)?.label ?? therapyType;
        const isGroup = therapyType === 'group';
        const cohortLabel = answers.groupSelection === 'help-find'
          ? 'Our team will help you find the right fit'
          : answers.requestedGroupName;
        return (
          <Card key={therapyType} withBorder radius="md" padding="md">
            <Text fw={600} size="sm" mb="sm">
              {therapyLabel}
            </Text>
            <Table verticalSpacing="xs" withRowBorders={false} highlightOnHover={false}>
              <Table.Tbody>
                {isGroup && cohortLabel ? (
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Group</Table.Th>
                    <Table.Td>{cohortLabel}</Table.Td>
                  </Table.Tr>
                ) : null}
                <Table.Tr>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Status</Table.Th>
                  <Table.Td>Requested — we&apos;ll contact you to schedule your screening call</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Setting</Table.Th>
                  <Table.Td>{formatPreferenceLabel(answers.format)}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>
        );
      })}

      {bookings.map((booking, idx) => {
        const provider = booking.selectedProviderId
          ? allProviders.find((p) => p.id === booking.selectedProviderId) ?? null
          : null;
        const isScreening = booking.isScreeningCall === true;
        const isWaitlisted = !isScreening && isWaitlistedBooking(booking, answers);
        const therapyLabel =
          THERAPY_TYPE_OPTIONS.find((o) => o.value === booking.therapyType)?.label ??
          booking.therapyType;
        const isConsultation = booking.sessionType === 'consultation';
        const appointmentKind = isConsultation ? 'consultation' : 'intake';
        const cardTitle = (() => {
          if (isWaitlisted) return `${therapyLabel} — waitlist`;
          if (!isScreening) return `${therapyLabel} ${appointmentKind}`;
          if (highRisk) return 'Intake coordinator call';
          const screeningLabels = answers.therapyTypes
            .filter((t) => t === 'group' || t === 'iop')
            .map((t) => (t === 'iop' ? 'IOP' : 'Group'));
          return screeningLabels.length > 0
            ? `${screeningLabels.join(' & ')} Intake Coordinator Call`
            : 'Intake coordinator call';
        })();
        const { date, time } = getBookingDateTimeLabels(booking);
        const setting = formatPreferenceLabel(answers.format, provider?.format);
        const providerLabel = provider
          ? `${provider.name}${provider.credentials ? `, ${provider.credentials}` : ''}`
          : '—';
        const durationLabel = isScreening || isConsultation ? '15 min' : '50 min';
        const cancelled = booking.cancelled === true;
        const costLabel =
          answers.payType === 'insurance' ? '$25 – $35 copay' : cashCostLabelFor(booking);
        const costRowLabel = answers.payType === 'insurance' ? 'Estimated cost' : 'Cost';

        if (isWaitlisted) {
          return (
            <Card
              key={idx}
              withBorder
              radius="md"
              padding="md"
              style={cancelled ? { opacity: 0.75 } : undefined}
            >
              <Flex justify="space-between" align="center" mb="sm">
                <Text fw={600} size="sm">
                  {cardTitle}
                </Text>
                {cancelled && (
                  <Text size="xs" c="red" fw={600}>
                    Removed
                  </Text>
                )}
              </Flex>
              {!cancelled && (
                <Alert color="gold" variant="light" radius="md" mb="sm">
                  No providers are currently available for this service. You&apos;ve been added to
                  our waitlist and we will contact you when a provider opens up.
                </Alert>
              )}
              <Table verticalSpacing="xs" withRowBorders={false} highlightOnHover={false}>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Status</Table.Th>
                    <Table.Td>On waitlist — no appointment scheduled</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Setting</Table.Th>
                    <Table.Td>{setting}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>{costRowLabel}</Table.Th>
                    <Table.Td>{costLabel}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                mt="sm"
                onClick={() => toggleWaitlisted(idx, booking.therapyType, !cancelled)}
              >
                {cancelled ? 'Restore waitlist request' : 'Remove from waitlist'}
              </Button>
            </Card>
          );
        }

        return (
          <Card
            key={idx}
            withBorder
            radius="md"
            padding="md"
            style={cancelled ? { opacity: 0.75 } : undefined}
          >
            <Flex justify="space-between" align="center" mb="sm">
              <Text fw={600} size="sm">
                {cardTitle}
              </Text>
              {cancelled && (
                <Text size="xs" c="red" fw={600}>
                  Cancelled
                </Text>
              )}
            </Flex>

            {isScreening && (
              <Text size="sm" c="dimmed" mb="sm">
                15-minute telehealth call with your intake coordinator
              </Text>
            )}

            {!isScreening && provider && (
              <Text size="sm" fw={500} mb="sm">
                {providerLabel}
              </Text>
            )}

            <Table verticalSpacing="xs" withRowBorders={false} highlightOnHover={false}>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Time</Table.Th>
                  <Table.Td>
                    {date} at {time}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Duration</Table.Th>
                  <Table.Td>{durationLabel}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Setting</Table.Th>
                  <Table.Td>{setting}</Table.Td>
                </Table.Tr>
                {(answers.availabilityTimeOfDay?.length > 0 || answers.availabilityDays?.length > 0) && (
                  <>
                    {answers.availabilityTimeOfDay?.length > 0 && (
                      <Table.Tr>
                        <Table.Th style={{ whiteSpace: 'nowrap' }}>Not available (time)</Table.Th>
                        <Table.Td>
                          {answers.availabilityTimeOfDay.map(availabilityTimeLabel).join(', ')}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {answers.availabilityDays?.length > 0 && (
                      <Table.Tr>
                        <Table.Th style={{ whiteSpace: 'nowrap' }}>Not available (days)</Table.Th>
                        <Table.Td>
                          {answers.availabilityDays.map(availabilityDaysLabel).join(', ')}
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </>
                )}
                {!isScreening && (
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>{costRowLabel}</Table.Th>
                    <Table.Td>{costLabel}</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              mt="sm"
              onClick={() => {
                if (onToggleBookingCancelled) {
                  onToggleBookingCancelled(idx, !cancelled, booking);
                } else {
                  dispatch({
                    type: 'SET_BOOKING_CANCELLED',
                    payload: { index: idx, cancelled: !cancelled },
                  });
                }
              }}
            >
              {cancelled ? 'Restore appointment' : 'Cancel appointment'}
            </Button>
          </Card>
        );
      })}

    </Stack>
  );
}

